/**
 * ARCaml Billing Frontend Spec - TypeScript Types & Interfaces
 * React TypeScript Implementation | Usage Warnings & Subscription UI
 */

// ============================================================================
// 1. ENUMS
// ============================================================================

/**
 * Platform plan types
 */
export enum PlatformPlan {
  ANNUAL = 'annual',
  MONTHLY = 'monthly',
}

/**
 * Usage warning severity levels
 */
export enum UsageWarningLevel {
  NONE = 'none',
  WARNING = 'warning',
  CRITICAL = 'critical',
}

/**
 * Subscription tiers
 */
export enum SubscriptionTier {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  ENTERPRISE = 'enterprise',
}

/**
 * Account status types
 */
export enum AccountStatus {
  ACTIVE = 'active',
  SUSPENDED = 'suspended',
  CANCELLED = 'cancelled',
  TRIAL = 'trial',
  EXPIRED = 'expired',
}

// ============================================================================
// 2. INTERFACES - BILLING DATA
// ============================================================================

/**
 * Subscription plan details
 */
export interface SubscriptionPlan {
  planId: string;
  planName: string;
  tier: SubscriptionTier;
  credits: number | null;
  monthlyLimit: number | null;
  monthlyRate: number | null;
  annualRate: number | null;
  discountPercentage?: number;
  features: readonly string[] | string[];
  maxUsers?: number;
  supportLevel: 'basic' | 'standard' | 'premium';
  description?: string;
  onboardings?: string;
  customerTypes?: string;
  ecdd?: string;
}

/**
 * Current subscription status
 */
export interface SubscriptionStatus {
  subscriptionId: string;
  planId: string;
  tier: SubscriptionTier;
  status: AccountStatus;
  startDate: string;
  endDate: string;
  autoRenewal: boolean;
  platformPlan: PlatformPlan;
}

/**
 * Usage data for current billing period
 */
export interface UsageData {
  subscriptionId: string;
  periodStartDate: string;
  periodEndDate: string;
  totalMonthlyCredit: number;
  creditsUsed: number;
  creditsRemaining: number;
  percentageUsed: number;
  percentageRemaining: number;
  dailyUsageBreakdown: {
    date: string;
    creditsUsed: number;
  }[];
}

/**
 * Billing invoice record
 */
export interface BillingInvoice {
  invoiceId: string;
  subscriptionId: string;
  invoiceDate: string;
  dueDate: string;
  amountDue: number;
  amountPaid: number;
  status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  itemsBreakdown: {
    description: string;
    quantity: number;
    rate: number;
    amount: number;
  }[];
  paidDate?: string;
  paymentMethod?: string;
}

/**
 * Payment method for billing
 */
export interface PaymentMethod {
  methodId: string;
  type: 'credit_card' | 'bank_transfer' | 'wire_transfer';
  isPrimary: boolean;
  isActive: boolean;
  displayName: string;
  lastFourDigits?: string;
  expiryDate?: string;
  bankName?: string;
}

// ============================================================================
// 3. INTERFACES - USAGE WARNING SYSTEM (CRITICAL)
// ============================================================================

/**
 * Usage threshold configuration for warning triggers
 * MANDATORY: Users must be alerted at 70%, 90%, and 100% thresholds
 */
export interface UsageThreshold {
  threshold: number; // 70, 90, 100
  level: UsageWarningLevel;
  messageKey: string; // i18n key for message
  isAutoAlert: boolean; // Whether to auto-trigger notification
}

/**
 * Usage warning message copy per threshold
 */
export interface UsageWarningMessage {
  threshold: number;
  level: UsageWarningLevel;
  messageCopy: string;
  actionRequired: boolean;
  suggestedAction?: string;
}

/**
 * Active usage warning state
 */
export interface ActiveUsageWarning {
  warningId: string;
  threshold: number;
  level: UsageWarningLevel;
  message: string;
  triggeredAt: string;
  dismissedAt?: string;
  isDismissed: boolean;
  actionUrl?: string;
  actionLabel?: string;
}

// ============================================================================
// 4. INTERFACES - SUBSCRIPTION UI STATE
// ============================================================================

/**
 * Billing page component state
 */
export interface BillingPageState {
  loading: boolean;
  error?: string;
  subscription: SubscriptionStatus | null;
  usage: UsageData | null;
  invoices: BillingInvoice[];
  paymentMethods: PaymentMethod[];
  activeWarnings: ActiveUsageWarning[];
  selectedInvoice?: BillingInvoice;
  showUpgradeModal: boolean;
  showPaymentModal: boolean;
}

/**
 * Upgrade flow state
 */
export interface UpgradeFlowState {
  currentTier: SubscriptionTier;
  selectedTier: SubscriptionTier;
  selectedPlan: PlatformPlan;
  currentRate: number;
  newRate: number;
  rateDifference: number;
  prorationCredit?: number;
  estimatedNewBalance: number;
  confirmationPending: boolean;
  upgradeInProgress: boolean;
}

/**
 * Purchase state
 */
export interface PurchaseState {
  requestId: string;
  purchaseDate: string;
  externalPurchaseId?: string;
  status: 'pending' | 'success' | 'failed' | 'cancelled';
  amount: number;
  tier: SubscriptionTier;
  platformPlan: PlatformPlan;
}

// ============================================================================
// 5. INTERFACES - SUBSCRIPTION DATA EXPORT
// ============================================================================

/**
 * Subscription details export response
 */
export interface CheckSubscriptionRequest {
  subscriptionId?: string;
  organizationId?: string;
}

export interface CheckSubscriptionResponse {
  success: boolean;
  subscription?: SubscriptionStatus;
  usage?: UsageData;
  plan?: SubscriptionPlan;
  warnings: ActiveUsageWarning[];
  nextBillingDate?: string;
}

/**
 * Usage export/download format
 */
export interface UsageExportData {
  subscriptionId: string;
  organizationName: string;
  periodStartDate: string;
  periodEndDate: string;
  totalCreditsAllocated: number;
  creditsUsed: number;
  creditsRemaining: number;
  dailyBreakdown: {
    date: string;
    creditsUsed: number;
    transactionCount?: number;
  }[];
  generatedAt: string;
}

// ============================================================================
// 6. INTERFACES - PLATFORM-SPECIFIC
// ============================================================================

/**
 * Plan configuration metadata
 */
export interface PlanMetadata {
  displayOrder: number;
  isPopular: boolean;
  isRecommended: boolean;
  badgeText?: string;
  description: string;
  highlights: string[];
}

/**
 * Billing configuration from backend
 */
export interface BillingConfig {
  enableBilling: boolean;
  enableSelfServeUpgrade: boolean;
  enableInvoiceDownload: boolean;
  enableAutoRenewal: boolean;
  currency: string;
  taxRate?: number;
  supportedPaymentMethods: string[];
  invoicePrefix: string;
  retentionDays: number;
}

// ============================================================================
// 7. TYPE UTILITIES
// ============================================================================

/**
 * Helper to determine if usage is critical
 */
export type CriticalUsageThreshold = Extract<UsageThreshold, { threshold: 90 | 100 }>;

/**
 * Usage warning event payload
 */
export interface UsageWarningEventPayload {
  warningId: string;
  threshold: number;
  percentageUsed: number;
  creditsRemaining: number;
  timestamp: string;
  userAction?: 'dismissed' | 'viewed' | 'acted_on';
}

/**
 * Billing event types for tracking
 */
export type BillingEventType =
  | 'subscription_created'
  | 'subscription_upgraded'
  | 'subscription_downgraded'
  | 'subscription_renewed'
  | 'subscription_cancelled'
  | 'usage_warning_triggered'
  | 'usage_warning_dismissed'
  | 'payment_attempted'
  | 'payment_successful'
  | 'payment_failed'
  | 'invoice_generated'
  | 'invoice_paid';

/**
 * Billing event for analytics
 */
export interface BillingEvent {
  eventType: BillingEventType;
  subscriptionId: string;
  timestamp: string;
  metadata?: Record<string, any>;
  userId: string;
  organizationId: string;
}
